import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { SnackbarProvider } from './context/SnackbarContext';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Login from './pages/Login';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const ConfigBuilder = lazy(() => import('./pages/ConfigBuilder'));
const UploadConfig = lazy(() => import('./pages/UploadConfig'));
const ConfigList = lazy(() => import('./pages/ConfigList'));
const ConfigDetail = lazy(() => import('./pages/ConfigDetail'));
const Compare = lazy(() => import('./pages/Compare'));
const ComponentLibrary = lazy(() => import('./pages/ComponentLibrary'));
const ComponentDetail = lazy(() => import('./pages/ComponentDetail'));
const GovernanceWorkbench = lazy(() => import('./pages/GovernanceWorkbench'));
const StandardsGuide = lazy(() => import('./pages/StandardsGuide'));
const GenerateConfig = lazy(() => import('./pages/GenerateConfig'));
const Profile = lazy(() => import('./pages/Profile'));

const Loading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Loading />;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function AppProviders() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <SnackbarProvider>
          <AuthProvider>
            <LanguageProvider>
              <Suspense fallback={<Loading />}>
                <Outlet />
              </Suspense>
            </LanguageProvider>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const router = createBrowserRouter([
  {
    element: <AppProviders />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/register', element: <Navigate to="/login" replace /> },
      {
        path: '/',
        element: <PrivateRoute><Layout /></PrivateRoute>,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <Dashboard /> },
          { path: 'configs', element: <ConfigList /> },
          { path: 'builder', element: <ConfigBuilder /> },
          { path: 'components', element: <ComponentLibrary /> },
          { path: 'governance', element: <GovernanceWorkbench /> },
          { path: 'standards', element: <StandardsGuide /> },
          { path: 'components/:id', element: <ComponentDetail /> },
          { path: 'configs/:id', element: <ConfigDetail /> },
          { path: 'upload', element: <UploadConfig /> },
          { path: 'compare', element: <Compare /> },
          { path: 'generate', element: <GenerateConfig /> },
          { path: 'competitors', element: <Navigate to="/dashboard" replace /> },
          { path: 'trends', element: <Navigate to="/dashboard" replace /> },
          { path: 'profile', element: <Profile /> }
        ]
      }
    ]
  }
]);

function App() {
  return <RouterProvider router={router} fallbackElement={<Loading />} />;
}

export default App;
